import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { MyBilling } from '../api/types';
import { ServiceBadge } from '../components/status';
import { Card, Empty, ErrorAlert, Spinner, Stat } from '../components/ui';
import { formatDateTime, formatNumber, formatRelative } from '../lib/format';

const KIND_LABEL: Record<string, string> = {
  HOTSPOT_USER: 'Usuario HotSpot',
  VOUCHER: 'Voucher',
  PPPOE_CLIENT: 'Cliente PPPoE',
  RENEWAL: 'Renovación',
};

function formatTotals(totals: Record<string, number>): string {
  const entries = Object.entries(totals);
  if (entries.length === 0) return '0';
  return entries.map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`).join(' + ');
}

export function MyBillingPage() {
  const billing = useQuery({
    queryKey: ['billing', 'my'],
    queryFn: () => api.get<MyBilling>('/billing/my'),
    refetchInterval: 60_000,
  });

  const data = billing.data;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Mi deuda</h1>
          <p>
            Cada usuario, voucher o cliente que creas suma su precio aquí. Borrarlo después
            no lo descuenta: ya fue creado. El contador vuelve a cero cuando el
            administrador marca tu pago como completado.
          </p>
        </div>
      </div>

      <ErrorAlert error={billing.error} />

      {billing.isLoading ? (
        <Spinner />
      ) : data ? (
        <>
          <div className="grid grid-stats" style={{ marginBottom: 18 }}>
            <Stat
              label="Debo ahora"
              value={formatTotals(data.pending.totals)}
              hint={
                data.pending.since
                  ? `acumulado desde ${formatRelative(data.pending.since)}`
                  : 'sin cargos pendientes'
              }
            />
            <Stat
              label="Creaciones pendientes de pago"
              value={formatNumber(data.pending.charges.length)}
              hint={`${formatNumber(data.pending.counts.hotspotUsers)} usuarios · ${formatNumber(data.pending.counts.vouchers)} vouchers · ${formatNumber(data.pending.counts.pppoeClients)} PPPoE · ${formatNumber(data.pending.counts.renewals)} renovaciones`}
            />
            <Stat
              label="Último pago"
              value={data.settlements[0] ? formatTotals(data.settlements[0].totals) : '—'}
              hint={
                data.settlements[0]
                  ? formatRelative(data.settlements[0].createdAt)
                  : 'todavía ninguno'
              }
            />
          </div>

          <Card title="Desglose pendiente">
            {data.pending.charges.length === 0 ? (
              <Empty>No debes nada ahora mismo. Todo lo creado está saldado.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Credencial</th>
                      <th>Plan</th>
                      <th className="num">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.charges.map((charge) => (
                      <tr key={charge.id}>
                        <td className="faint">{formatDateTime(charge.createdAt)}</td>
                        <td>
                          {KIND_LABEL[charge.kind] ?? charge.kind}{' '}
                          <ServiceBadge service={charge.service} />
                        </td>
                        <td className="mono">{charge.username}</td>
                        <td>{charge.planName ?? <span className="faint">—</span>}</td>
                        <td className="num">
                          {charge.amount.toFixed(2)} {charge.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {data.settlements.length > 0 && (
            <Card title="Pagos completados">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th className="num">Creaciones</th>
                      <th className="num">Importe</th>
                      <th>Marcado por</th>
                      <th>Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.settlements.map((settlement) => (
                      <tr key={settlement.id}>
                        <td>{formatDateTime(settlement.createdAt)}</td>
                        <td className="num">{formatNumber(settlement.chargesCount)}</td>
                        <td className="num">
                          <strong>{formatTotals(settlement.totals)}</strong>
                        </td>
                        <td className="faint">{settlement.settledByEmail}</td>
                        <td className="faint">{settlement.note ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      ) : null}
    </main>
  );
}
